import { z } from 'zod';
import { emailSchema, localeSchema, slugSchema, uuidSchema } from './common.js';
/** Rollen je Mandant (Spec 4). Plattform-Admin haengt am User, nicht am Mandanten. */
export const TENANT_ROLES = ['admin', 'agent', 'viewer'];
export const tenantRoleSchema = z.enum(TENANT_ROLES);
/**
 * Mandanten-Settings (Spec 3.2). Bewusst klein gehalten — spaetere Phasen
 * ergaenzen Felder (slack_webhook_url in Phase 5, ai_* in Phase 4).
 */
export const tenantSettingsSchema = z.object({
    locale: localeSchema.default('de'),
    csat_enabled: z.boolean().default(false),
    ai_enabled: z.boolean().default(false),
    /**
     * Slack-Incoming-Webhook je Mandant (Spec 3.2 / 8.5). `null` = kein Slack;
     * der Kanal faellt dann still aus, auch wenn ein Mitglied ihn abonniert hat.
     * Kein Geheimnis im Sinne von Spec 11 (die URL *ist* die Berechtigung), aber
     * sie wird nur an `tenant.manage` ausgeliefert.
     */
    slack_webhook_url: z.url().max(500).nullable().default(null),
});
export const DEFAULT_TENANT_SETTINGS = {
    locale: 'de',
    csat_enabled: false,
    ai_enabled: false,
    slack_webhook_url: null,
};
/**
 * Die Settings, wie sie ein Mitglied **ohne** `tenant.manage` sehen darf.
 *
 * `slack_webhook_url` faellt heraus: die URL *ist* die Berechtigung, in diesen
 * Slack-Kanal zu schreiben (Spec 11 sinngemaess — sie steht nur nicht in der
 * Liste der verschluesselten Felder, weil sie kein Zugangsdatum eines
 * Anbieters ist). Ein Agent oder Viewer braucht sie nicht.
 */
export function redactTenantSettings(settings) {
    return { ...settings, slack_webhook_url: null };
}
/**
 * Settings einer gespeicherten Zeile lesen. Aeltere Mandanten haben Felder
 * spaeterer Phasen (`slack_webhook_url`) nicht in der Spalte — ohne diesen
 * Durchlauf waeren sie zur Laufzeit `undefined`.
 */
export function normalizeTenantSettings(value) {
    const parsed = tenantSettingsSchema.safeParse(value ?? {});
    return parsed.success ? parsed.data : DEFAULT_TENANT_SETTINGS;
}
/**
 * Mandantenweite Mail-Vorgaben (ADR 0017) — eigene **Spalten**, nicht Teil von
 * `settings`.
 *
 * Der Grund ist die referenzielle Haerte: `default_mail_connection_id` traegt
 * einen Foreign Key mit `ON DELETE RESTRICT`, damit eine benutzte Verbindung
 * nicht verschwinden kann. In einem jsonb-Feld gaebe es den nicht.
 *
 * Zusammen erledigen die drei Felder den eigentlichen Zweck des Vaults: eine
 * neue E-Mail-Queue kann sofort verschicken, ohne dass jemand Zugangsdaten
 * oder einen Absender eintippt.
 */
export const tenantMailDefaultsSchema = z.object({
    default_mail_connection_id: uuidSchema.nullable().default(null),
    default_from_address: emailSchema.nullable().default(null),
    default_from_name: z.string().min(1).max(200).nullable().default(null),
});
/**
 * Dieselben drei Felder als **Eingabe**. Eigenes Schema statt `.partial()`,
 * weil `.default(null)` sonst auch bei weggelassenem Schluessel greift — der
 * Patch enthielte dann immer alle drei Felder, und das Audit-Log meldete
 * Aenderungen, die niemand gemacht hat.
 */
export const tenantMailDefaultsInputSchema = z.object({
    default_mail_connection_id: uuidSchema.nullable().optional(),
    default_from_address: emailSchema.nullable().optional(),
    default_from_name: z.string().min(1).max(200).nullable().optional(),
});
export const tenantSchema = z
    .object({
    id: uuidSchema,
    name: z.string().min(1).max(200),
    slug: slugSchema,
    settings: tenantSettingsSchema,
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
})
    .extend(tenantMailDefaultsSchema.shape);
/**
 * Mandant in der Betreiber-Uebersicht („Plattform → Mandanten").
 *
 * Die Mitglieder-Zahl steht nicht in `tenantSchema`, weil sie kein Feld des
 * Mandanten ist, sondern eine Aggregation ueber `tenant_members` — sie waere
 * auf jedem anderen Weg (Session, Detail-Route) nur totes Gewicht.
 */
export const platformTenantSchema = tenantSchema.extend({
    member_count: z.number().int().nonnegative(),
});
export const createTenantSchema = z.object({
    name: z.string().min(1).max(200),
    slug: slugSchema,
    settings: tenantSettingsSchema.partial().optional(),
});
export const updateTenantSchema = createTenantSchema
    .partial()
    .extend(tenantMailDefaultsInputSchema.shape);
/**
 * Ein Scope-Eintrag schraenkt ein Mitglied auf eine Queue oder eine Quelle ein.
 * Genau eines von beiden ist gesetzt. Keine Scopes = Zugriff auf alles im Mandanten.
 * Queues/Sources existieren erst ab Phase 1 — die IDs bleiben hier bewusst untypisiert.
 */
export const memberScopeSchema = z
    .object({
    queue_id: uuidSchema.nullable().default(null),
    source_id: uuidSchema.nullable().default(null),
})
    .refine((scope) => (scope.queue_id === null) !== (scope.source_id === null), {
    message: 'Ein Scope referenziert genau eine Queue oder genau eine Quelle.',
});
export const tenantMemberSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    user_id: uuidSchema,
    role: tenantRoleSchema,
    scopes: z.array(memberScopeSchema),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/** Der plattformweite User hinter einer Mitgliedschaft — fuer die Mitgliederliste. */
export const memberUserSchema = z.object({
    id: uuidSchema,
    email: emailSchema,
    name: z.string(),
    image: z.string().nullable(),
    two_factor_enabled: z.boolean(),
});
export const tenantMemberDetailSchema = tenantMemberSchema.extend({
    user: memberUserSchema,
});
/** Mitglied anlegen: entweder bestehender User (user_id) oder Einladung per E-Mail. */
export const createTenantMemberSchema = z
    .object({
    user_id: uuidSchema.optional(),
    email: emailSchema.optional(),
    role: tenantRoleSchema,
    scopes: z.array(memberScopeSchema).default([]),
})
    .refine((input) => Boolean(input.user_id) !== Boolean(input.email), {
    message: 'Entweder user_id oder email angeben, nicht beides.',
});
/**
 * Wurde fuer die Einladung ein neuer User angelegt, enthaelt `initial_password`
 * das Startpasswort — **einmalig** bei der Erzeugung, danach nie wieder
 * (gleiche Regel wie fuer API-Keys, Spec 11).
 */
export const createTenantMemberResultSchema = z.object({
    member: tenantMemberDetailSchema,
    initial_password: z.string().nullable(),
});
export const updateTenantMemberSchema = z.object({
    role: tenantRoleSchema.optional(),
    scopes: z.array(memberScopeSchema).optional(),
});
export const teamSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    name: z.string().min(1).max(200),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createTeamSchema = z.object({
    name: z.string().min(1).max(200),
    member_ids: z.array(uuidSchema).default([]),
});
export const updateTeamSchema = createTeamSchema.partial();
//# sourceMappingURL=tenant.js.map